import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAnalyticsEvents } from "@/hooks/useAnalyticsEvents";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, Globe, Monitor, Smartphone, Tablet } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { de } from "date-fns/locale";
import { motion, AnimatePresence } from "framer-motion";

export const LiveUsersWidget = () => {
  // Fetch events from last 5 minutes
  const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
  const { data: events, isLoading } = useAnalyticsEvents({ 
    limit: 100,
    startDate: fiveMinutesAgo
  });

  // Group by session_id to get unique active users
  const activeUsers = events ? [...new Map(
    events.map(event => [event.session_id, event])
  ).values()] : [];

  const getDeviceIcon = (deviceType?: string) => {
    switch (deviceType?.toLowerCase()) {
      case 'mobile':
        return <Smartphone className="h-3 w-3" />;
      case 'tablet':
        return <Tablet className="h-3 w-3" />;
      default:
        return <Monitor className="h-3 w-3" />;
    }
  };

  const getCountryFlag = (country?: string) => {
    if (!country || country === 'Unknown') return '🌍';
    
    const countryFlags: Record<string, string> = {
      'DE': '🇩🇪',
      'US': '🇺🇸',
      'GB': '🇬🇧',
      'FR': '🇫🇷',
      'ES': '🇪🇸',
      'IT': '🇮🇹',
      'AT': '🇦🇹',
      'CH': '🇨🇭',
    };
    
    return countryFlags[country] || '🌍';
  };

  if (isLoading) {
    return (
      <Card className="border-accent/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="h-5 w-5 text-accent" />
            Live Besucher
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[200px]" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-accent/20 bg-card/50 backdrop-blur-sm">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="h-5 w-5 text-accent" />
            Live Besucher
          </CardTitle>
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Badge 
              variant="outline" 
              className="text-lg font-bold border-accent/50 bg-accent/10 text-accent"
            >
              <motion.span
                key={activeUsers.length}
                initial={{ scale: 1.3, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                {activeUsers.length}
              </motion.span>
            </Badge>
          </motion.div>
        </div>
        <CardDescription className="text-xs">
          Aktive Nutzer in den letzten 5 Minuten
        </CardDescription>
      </CardHeader>
      <CardContent>
        {activeUsers.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            Keine aktiven Besucher im Moment
          </div>
        ) : (
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            <AnimatePresence mode="popLayout">
              {activeUsers.map((event, index) => {
                const properties = (event.properties || {}) as Record<string, any>;
                const deviceType = properties.device_type || 'desktop';
                const country = properties.country || 'Unknown';
                const city = properties.city;
                
                return (
                  <motion.div
                    key={event.session_id || event.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center justify-between p-3 rounded-lg bg-accent/5 hover:bg-accent/10 border border-accent/10 transition-colors"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{getCountryFlag(country)}</span>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          {getDeviceIcon(deviceType)}
                        </div>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="secondary" className="text-xs font-normal">
                            {country}
                          </Badge>
                          {city && (
                            <span className="text-xs text-muted-foreground truncate">
                              {city}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground truncate font-mono">
                          {event.page_path || '/'}
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-xs text-muted-foreground whitespace-nowrap ml-2">
                      {formatDistanceToNow(new Date(event.created_at), { 
                        addSuffix: true,
                        locale: de 
                      })}
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
        
        {activeUsers.length > 0 && (
          <div className="mt-4 pt-3 border-t border-accent/10">
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Globe className="h-3 w-3" />
                <span>
                  {[...new Set(activeUsers.map(e => {
                    const props = (e.properties || {}) as Record<string, any>;
                    return props.country || 'Unknown';
                  }))].length} Länder
                </span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Monitor className="h-3 w-3" />
                <span>
                  {activeUsers.filter(e => {
                    const props = (e.properties || {}) as Record<string, any>;
                    return props.device_type === 'desktop';
                  }).length} Desktop
                </span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
